import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Checkbox } from "@mui/material";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import DateConversion from "../../../../../utils/DateConversion";
import { useCurrencySymbol } from "../../../../../utils/currencyUtils";
import CommaConvert from "../../../../../utils/CommaConvert";
import { markDescriptionComplete, markHardwarePartComplete, markNotDescriptionComplete, markNotHardwarePartComplete } from "../../../../../store/slices/ProjectOngoing/projectOngoingSlice";
import { fetchHardwareProjectTracking } from "../../../../../store/slices/hardwareDescriptionSlice";
import { fetchDescriptionTasksProjectTracking } from "../../../../../store/slices/DescriptionTask";
import { fetchWorkEntriesProjectTracking } from "../../../../../store/slices/workentriesSlice";
import { updateOngoingDescription, updateOngoingHardwareParts } from "../../../../../store/slices/ProjectOngoing/ProjectOngoingUpdateSlice";
import ProgressTracker from "../../../../../utils/ProgressTracker";
import LoadingSpinner from "../../../../../components/common/loader/LoadingSpinner";
import {
  consolidateTrainingExpenseRowsForDisplay,
  isTrainingExpenseDisplayRow,
  resolveTrainingAssignedDate,
  resolveTrainingAssigneeId,
  resolveTrainingEndDate,
  trainingMatchesEmployeeProject,
  unwrapTrainings,
} from "../../../../../utils/trainingOtherExpenseSync";
import useToolGuideSync from "../../../../../features/toolGuide/useToolGuideSync";
import { MANAGER_BUCKET_KEYS } from "../../../../../features/toolGuide/toolGuideConfig";

const isDescriptionTaskComplete = (entry) =>
  entry?.isTaskComplete === true || entry?.isTaskcomplete === true;

const normalizeOtherExpenseDescription = (description) =>
  String(description ?? "").trim();

const descriptionsMatch = (left, right) =>
  normalizeOtherExpenseDescription(left) ===
  normalizeOtherExpenseDescription(right);

const parseExpenseNumber = (val) => {
  if (val == null || val === "") return null;
  const parsed = Number(String(val).replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
};

/** Manager hardware: save expenses before mark-complete when row is OP-only (no manager task row). */
export async function persistManagerHardwareOtherExpenseBeforeComplete({
  dispatch,
  projectId,
  entry,
  entriesInGroup,
  allDescriptionEntriesForProject,
  loggedUserId,
  currentExpenses,
  startDate,
  endDate,
  editedDescription,
  editedDescriptionNotes,
  persistManagerConsolidatedExpense,
}) {
  const sourceList =
    allDescriptionEntriesForProject?.length > 0
      ? allDescriptionEntriesForProject
      : entriesInGroup;
  const descTrim = normalizeOtherExpenseDescription(entry?.description);
  const group =
    (sourceList || []).filter((e) =>
      descriptionsMatch(e?.description, descTrim)
    ) || entriesInGroup || [entry];
  const opEntries = group.filter(
    (e) => String(e?.taskownerid || "") !== String(loggedUserId || "")
  );
  const managerEntry = group.find(
    (e) => String(e?.taskownerid || "") === String(loggedUserId || "")
  );

  if (managerEntry?._id && persistManagerConsolidatedExpense) {
    const saved = await persistManagerConsolidatedExpense(
      entry._id,
      entry,
      currentExpenses
    );
    if (!saved) return false;
    for (const opEntry of opEntries) {
      if (isDescriptionTaskComplete(opEntry)) continue;
      const entryValue =
        parseExpenseNumber(opEntry?.value) ?? (Number(opEntry?.value) || 0);
      const storedOpExpenses = parseExpenseNumber(opEntry?.expenses);
      const expensesToSave =
        storedOpExpenses != null && storedOpExpenses > 0
          ? storedOpExpenses
          : entryValue;
      const notesToSave =
        editedDescriptionNotes?.[entry._id] ?? entry.notes ?? opEntry.notes;
      await dispatch(
        updateOngoingDescription({
          projectId,
          id: opEntry._id,
          updatedData: {
            value: entryValue,
            expenses: expensesToSave,
            notes: notesToSave,
            typeservice: opEntry.typeservice,
            startdate: startDate ?? opEntry?.startdate ?? null,
            enddate: endDate ?? opEntry?.enddate ?? null,
          },
        })
      ).unwrap();
    }
    return true;
  }

  if (!opEntries.length) {
    return persistManagerConsolidatedExpense
      ? persistManagerConsolidatedExpense(entry._id, entry, currentExpenses)
      : false;
  }

  for (const opEntry of opEntries) {
    if (isDescriptionTaskComplete(opEntry)) continue;
    const expenseToSave =
      editedDescription?.[entry._id] !== undefined
        ? parseExpenseNumber(editedDescription[entry._id])
        : currentExpenses;
    const notesToSave =
      editedDescriptionNotes?.[entry._id] ?? entry.notes ?? opEntry.notes;
    await dispatch(
      updateOngoingDescription({
        projectId,
        id: opEntry._id,
        updatedData: {
          value: parseExpenseNumber(opEntry?.value) ?? (Number(opEntry?.value) || 0),
          expenses:
            expenseToSave ?? parseExpenseNumber(opEntry?.value) ?? (Number(opEntry?.value) || 0),
          notes: notesToSave,
          typeservice: opEntry.typeservice,
          startdate: startDate ?? opEntry?.startdate ?? null,
          enddate: endDate ?? opEntry?.enddate ?? null,
        },
      })
    ).unwrap();
  }
  return true;
}

/** Mark every hardware other-expense row in the same description group complete/incomplete. */
export async function markHardwareOtherExpenseGroupComplete({
  dispatch,
  projectId,
  entriesInGroup,
  managerEndDate,
  managerStartDate,
  complete,
  loggedUserId,
  skipPreUpdate = false,
}) {
  const toUpdate = (entriesInGroup || []).filter((e) => e?._id);
  if (!toUpdate.length) return true;

  if (complete) {
    if (!skipPreUpdate) {
      for (const entry of toUpdate) {
        if (isDescriptionTaskComplete(entry)) continue;

        const entryValue =
          parseExpenseNumber(entry?.value) ?? (Number(entry?.value) || 0);
        const storedExpenses = parseExpenseNumber(entry?.expenses);
        const expensesToSave =
          storedExpenses != null && storedExpenses > 0 ? storedExpenses : entryValue;

        if (expensesToSave > 0 || entryValue > 0) {
          await dispatch(
            updateOngoingDescription({
              projectId,
              id: entry._id,
              updatedData: {
                value: entryValue,
                expenses: expensesToSave > 0 ? expensesToSave : entryValue,
                startdate: managerStartDate ?? entry?.startdate ?? null,
                enddate: managerEndDate ?? entry?.enddate ?? null,
              },
            })
          ).unwrap();
        }
      }
    }

    for (const entry of toUpdate) {
      if (isDescriptionTaskComplete(entry)) continue;
      try {
        await dispatch(
          markDescriptionComplete({ projectid: projectId, taskid: entry._id })
        ).unwrap();
      } catch (err) {
        const msg = String(err || "").toLowerCase();
        if (!msg.includes("already") && !msg.includes("complete")) {
          throw err;
        }
      }
    }
  } else {
    for (const entry of toUpdate) {
      if (!isDescriptionTaskComplete(entry)) continue;
      try {
        await dispatch(
          markNotDescriptionComplete({ projectid: projectId, taskid: entry._id })
        ).unwrap();
      } catch (err) {
        const msg = String(err || "").toLowerCase();
        if (!msg.includes("already") && !msg.includes("incomplete")) {
          throw err;
        }
      }
    }
  }

  dispatch(fetchWorkEntriesProjectTracking());
  dispatch(fetchDescriptionTasksProjectTracking());
  dispatch(fetchHardwareProjectTracking());
  return true;
}

const OPHardwareOngoingPM = ({
    refresh,
    setIsLoading,
    index,
    row,
    hardwareProjectTrackingParts,
    hardwareDescriptions,
    loggedUser,
    showListState,
    isViewOnly = false,
    allDescriptionEntriesForProject = [],
    isManagerHardwareConsolidatedView = false,
}) => {
    const { formatDateByCountry, getDatePlaceholder, getDateFormat } =
        DateConversion();
      const dispatch = useDispatch();
      const { syncToolGuideStep } = useToolGuideSync(
        MANAGER_BUCKET_KEYS.PROJECT_ONGOING,
        "L4_PM"
      );
      const { enqueueSnackbar } = useSnackbar();
      const currency = useCurrencySymbol();
      const USAComma = CommaConvert();
      const { mytrainings } = useSelector((state) => state.myTrainings);
    
      const [loadingRows, setLoadingRows] = useState({});
      const [descriptionCompleteChecked, setDescriptionCompleteChecked] =
        useState({});

      const displayHardwareDescriptions = useMemo(() => {
        const base = Array.isArray(hardwareDescriptions) ? hardwareDescriptions : [];
        const resolveRefId = (ref) => {
          if (ref == null || ref === "") return "";
          if (typeof ref === "object") return String(ref?._id ?? ref?.id ?? "").trim();
          return String(ref).trim();
        };
        const normKey = (v) => String(v || "").trim().toLowerCase();
        const pick = (...vals) => vals.find((v) => v != null && String(v).trim() !== "");
        const trainingsList = unwrapTrainings(mytrainings);
        const assigneeId = resolveRefId(loggedUser?._id || loggedUser?.id);
        const assigneeEmail = normKey(pick(loggedUser?.EmpEmail, loggedUser?.email, loggedUser?.Email));
        const assigneeName = normKey(
          [loggedUser?.EmpFirstName, loggedUser?.EmpLastName].filter(Boolean).join(" ") ||
            pick(loggedUser?.name, loggedUser?.fullName)
        );

        if (!trainingsList.length || (!assigneeId && !assigneeEmail && !assigneeName)) {
          return consolidateTrainingExpenseRowsForDisplay(base);
        }

        const rowLeadLike = row?.lead || row?.Lead || {};
        const projectMeta = {
          assigneeEmail,
          rowId: row?._id,
          projectIds: [row?._id, row?.ProjectID, row?.projectid, row?.projectId, rowLeadLike?._id].filter(Boolean),
          quotationIds: [
            row?.quotationId,
            row?.quotationID,
            row?.quotationid,
            row?.QuotationId,
            rowLeadLike?.quotationId,
            rowLeadLike?.quotationID,
            rowLeadLike?.quotationid,
          ].filter(Boolean),
          projectNames: [row?.ProjectName, row?.projectname, rowLeadLike?.ProjectName, rowLeadLike?.projectname].filter(Boolean),
          leadIds: [row?.leadid, row?.leadId, row?.LeadId, row?.LeadID, rowLeadLike?.leadid, rowLeadLike?.leadId].filter(Boolean),
        };

        const matched = trainingsList.filter((training) => {
          const assignedRaw = training?.assignedto ?? training?.assignedTo;
          const assignedId = resolveTrainingAssigneeId(assignedRaw);
          const assignedEmail =
            typeof assignedRaw === "string" && assignedRaw.includes("@")
              ? normKey(assignedRaw)
              : normKey(pick(training?.assignedToEmail, training?.assigneeEmail, assignedRaw?.EmpEmail, assignedRaw?.email));
          const assignedName = normKey(
            typeof assignedRaw === "string"
              ? assignedRaw
              : [assignedRaw?.EmpFirstName || assignedRaw?.firstName, assignedRaw?.EmpLastName || assignedRaw?.lastName]
                  .filter(Boolean)
                  .join(" ") || pick(assignedRaw?.name, assignedRaw?.fullName)
          );
          const assigneeMatches =
            (assigneeId && assignedId && String(assignedId) === String(assigneeId)) ||
            (assigneeEmail && assignedEmail && assignedEmail === assigneeEmail) ||
            (assigneeName && assignedName && assignedName === assigneeName);
          if (!assigneeMatches) return false;

          if (trainingMatchesEmployeeProject(training, assigneeId || assigneeEmail || assigneeName, projectMeta)) {
            return true;
          }

          const trainingProjectName = normKey(pick(training?.projectname, training?.ProjectName));
          const rowProjectNames = projectMeta.projectNames.map((name) => normKey(name));
          return Boolean(trainingProjectName && rowProjectNames.includes(trainingProjectName));
        });

        const normalized = matched
          .map((training) => {
            const amount = Number(
              String(training?.trainingcost ?? training?.trainingCost ?? 0).replace(/,/g, "")
            );
            const notes = training?.notes ?? training?.note ?? "";
            const startdate = resolveTrainingAssignedDate(training, row?.DateRequested);
            const enddate = resolveTrainingEndDate(training);
            return {
              amount: Number.isFinite(amount) ? amount : 0,
              notes: String(notes || "").trim(),
              startdate,
              enddate,
            };
          })
          .filter((entry) => entry.amount > 0 || entry.notes);

        const total = normalized.reduce((sum, entry) => sum + (Number(entry.amount) || 0), 0);
        const joinedNotes = Array.from(new Set(normalized.map((entry) => entry.notes).filter(Boolean))).join("; ");
        if (total <= 0 && !joinedNotes) {
          return consolidateTrainingExpenseRowsForDisplay(base);
        }

        const lockedTrainingRow = {
          _id: `training_cost_${assigneeId || assigneeEmail || assigneeName || "selected"}`,
          taskownerid: assigneeId || assigneeEmail || assigneeName,
          projectid: row?._id,
          description: "Training Cost",
          value: total,
          notes: joinedNotes,
          startdate: normalized.map((entry) => entry.startdate).find(Boolean) ?? row?.DateRequested ?? null,
          enddate: normalized.map((entry) => entry.enddate).find(Boolean) ?? null,
          expenses: total,
          isTaskComplete: true,
          _isTrainingCost: true,
        };

        if (base.some((entry) => isTrainingExpenseDisplayRow(entry))) {
          return consolidateTrainingExpenseRowsForDisplay(base);
        }

        return consolidateTrainingExpenseRowsForDisplay([...base, lockedTrainingRow]);
      }, [hardwareDescriptions, loggedUser, mytrainings, row]);

      useEffect(() => {
        setDescriptionCompleteChecked((prev) => {
          if (!Object.keys(prev).length) return prev;
          const next = { ...prev };
          let changed = false;
          (displayHardwareDescriptions || []).forEach((entry) => {
            const rowKey = entry?._id;
            if (!rowKey || next[rowKey] === undefined) return;
            const serverComplete = isDescriptionTaskComplete(entry);
            if (serverComplete === next[rowKey]) {
              delete next[rowKey];
              changed = true;
            }
          });
          return changed ? next : prev;
        });
      }, [displayHardwareDescriptions]);
    
      const handleHardwarePartsMarkComplete = async (entry) => {
        if (isViewOnly) return;
        syncToolGuideStep("view-details");
        const projectid = row?._id;
        const taskid = entry?._id;
        setLoadingRows((prev) => ({ ...prev, [entry?._id]: true }));
        setIsLoading(true);
        try {
          await dispatch(markHardwarePartComplete({ projectid, taskid }));
          dispatch(fetchHardwareProjectTracking());
          dispatch(fetchDescriptionTasksProjectTracking());
          enqueueSnackbar("Part Completed Successfully", {
            variant: "success",
          });
        } catch {
          enqueueSnackbar("Failed To Complete Task", { variant: "error" });
        } finally {
          setLoadingRows((prev) => ({ ...prev, [entry?._id]: false }));
          setIsLoading(false);
        }
      };
    
      const handleMarkNonCompleteHardware = async (entry) => {
        if (isViewOnly) return;
        const projectid = row?._id;
        const taskid = entry?._id;
        setLoadingRows((prev) => ({ ...prev, [entry?._id]: true }));
        setIsLoading(true);
        try {
          await dispatch(markNotHardwarePartComplete({ projectid, taskid }));
          dispatch(fetchHardwareProjectTracking());
          dispatch(fetchDescriptionTasksProjectTracking());
          enqueueSnackbar("Part Not-Completed Successfully", {
            variant: "success",
          });
        } catch {
          enqueueSnackbar("Failed To Non-Complete Task", { variant: "error" });
        } finally {
          setLoadingRows((prev) => ({ ...prev, [entry?._id]: false }));
          setIsLoading(false);
        }
      };
    
      const [updatedHardwareEntries, setUpdatedHardwareEntries] = useState({});
      const handleDateSelfEntryHardware = useCallback(
        async (entry) => {
          if (isViewOnly) return;
          syncToolGuideStep("view-details");
          const matchedEntry = updatedHardwareEntries[entry._id] || {}; // safely get data
          const matchedStartDate = matchedEntry.startdate || null;
          const matchedEndDate = matchedEntry.enddate || null;
          const updatedData = {
            description: entry.assemblepartdescription,
            startdate: matchedStartDate ?? entry.startdate ?? null,
            enddate: matchedEndDate ?? entry.enddate ?? null,
            // startdate: matchedEntry.startdate|| matchedStartDate,
            // enddate: matchedEntry.enddate || matchedEndDate,
          };
          try {
            await dispatch(
              updateOngoingHardwareParts({
                projectId: row?._id,
                id: entry._id,
                updatedData,
              })
            );
            dispatch(fetchWorkEntriesProjectTracking());
            dispatch(fetchDescriptionTasksProjectTracking());
          } catch {
            enqueueSnackbar("Failed To Non-Complete Task", { variant: "error" });
          } finally {
            refresh();
            setLoadingRows((prev) => ({ ...prev, [entry?._id]: false }));
            setIsLoading(false);
          }
        },
        [
          isViewOnly,
          updatedHardwareEntries,
          row,
          dispatch,
          setIsLoading,
          refresh,
          enqueueSnackbar,
          syncToolGuideStep,
        ]
      );
      const handleDateChange = useCallback(
        (date, field, entry) => {
          if (isViewOnly) return;
          setUpdatedHardwareEntries((prev) => ({
            ...prev,
            [entry._id]: {
              ...prev[entry._id],
              [field]: date,
            },
          }));
          if (field === "enddate") {
            const newentry = {
              ...entry,
              enddate: date,
            };
            handleDateSelfEntryHardware(newentry);
          }
        },
        [isViewOnly, handleDateSelfEntryHardware]
      );
    
      const [updatedDateDescription, setUpdatedDateDescription] = useState({});
      const handleDescriptionDateSubmit = useCallback(
        async (entry) => {
          if (isViewOnly) return;
          syncToolGuideStep("view-details");
          const matchedEntry = updatedDateDescription[entry._id] || {};
          const matchedStartDate = matchedEntry.startdate || null;
          const matchedEndDate = matchedEntry.enddate || null;
    
          const updatedData = {
            description: entry.description,
            value: entry.value,
            notes: entry.notes,
            typeservice: entry.typeservice,
            // startdate: matchedEntry.startdate || matchedStartDate,
            // enddate: matchedEntry.enddate || matchedEndDate,
            startdate: matchedStartDate ?? entry.startdate ?? null,
            enddate: matchedEndDate ?? entry.enddate ?? null,
          };
    
          try {
            await dispatch(
              updateOngoingDescription({
                projectId: row?._id,
                id: entry._id,
                updatedData,
              })
            );
            dispatch(fetchWorkEntriesProjectTracking());
            dispatch(fetchDescriptionTasksProjectTracking());
          } catch {
            enqueueSnackbar("Failed To Add Date", { variant: "error" });
          } finally {
            setLoadingRows((prev) => ({ ...prev, [entry?._id]: false }));
            setIsLoading(false);
          }
        },
        [isViewOnly, updatedDateDescription, row, dispatch, enqueueSnackbar, setIsLoading, syncToolGuideStep]
      );
    
      const handleDescriptionDateChange = useCallback(
        (date, field, entry) => {
          if (isViewOnly) return;
          setUpdatedDateDescription((prev) => ({
            ...prev,
            [entry._id]: {
              ...prev[entry._id],
              [field]: date,
            },
          }));
    
          if (field === "enddate") {
            const newentry = {
              ...entry,
              enddate: date,
            };
            handleDescriptionDateSubmit(newentry);
          }
        },
        [isViewOnly, handleDescriptionDateSubmit]
      );
    
      const [editedDescription, setEditedDescription] = useState({});
      const [editedDescriptionNotes, setEditedDescriptionNotes] = useState({});
    
      const handleDescriptionExpensesUpdate = useCallback(
        async (entryId, { manageLoading = true } = {}) => {
          if (isViewOnly) return false;
          syncToolGuideStep("view-details");
          const value = editedDescription[entryId];
          if (!entryId || value === undefined) return true;
          const updatedData = {
            expenses: Number(String(value).replace(/,/g, "")),
          };
          if (manageLoading) {
            setLoadingRows((prev) => ({ ...prev, [entryId]: true }));
            setIsLoading(true);
          }
          try {
            await dispatch(
              updateOngoingDescription({
                projectId: row?._id,
                id: entryId,
                updatedData,
              })
            );
            dispatch(fetchWorkEntriesProjectTracking());
            dispatch(fetchDescriptionTasksProjectTracking());
            dispatch(fetchHardwareProjectTracking());
            return true;
          } catch {
            enqueueSnackbar("Failed To Add Date", { variant: "error" });
            return false;
          } finally {
            if (manageLoading) {
              setLoadingRows((prev) => ({ ...prev, [entryId]: false }));
              setIsLoading(false);
            }
          }
        },
        [isViewOnly, editedDescription, row?._id, dispatch, enqueueSnackbar, setIsLoading, syncToolGuideStep]
      );
    
      const handleDescriptionNotesUpdate = async (entryId) => {
        if (isViewOnly) return;
        const value = editedDescriptionNotes[entryId]; // get the value for this specific task
        if (!entryId) return;
        const updatedData = {
          notes: value,
        };
        try {
          await dispatch(
            updateOngoingDescription({
              projectId: row?._id,
              id: entryId,
              updatedData,
            })
          );
          dispatch(fetchWorkEntriesProjectTracking());
          dispatch(fetchDescriptionTasksProjectTracking());
        } catch {
          enqueueSnackbar("Failed To Add Date", { variant: "error" });
        } finally {
          setLoadingRows((prev) => ({ ...prev, [entryId]: false }));
          setIsLoading(false);
        }
      };
    
      const handleHardwareQuantityUpdate = async (entryId) => {
        if (isViewOnly) return;
        syncToolGuideStep("view-details");
        if (!entryId) return;
    
        const entryData = updatedHardwareEntries[entryId];
        if (!entryData) return;
    
        const updatedData = {
          quantity: entryData.quantity,
          totalprice: entryData.totalprice,
        };
        console.log("handleHardwareQuantityUpdate entryId", entryId, updatedData);
        try {
          setLoadingRows((prev) => ({ ...prev, [entryId]: true }));
    
          await dispatch(
            updateOngoingHardwareParts({
              projectId: row?._id,
              id: entryId,
              updatedData,
            })
          );
    
          dispatch(fetchWorkEntriesProjectTracking());
          dispatch(fetchDescriptionTasksProjectTracking());
        } catch (error) {
          enqueueSnackbar("Failed to update quantity", { variant: "error" });
        } finally {
          setLoadingRows((prev) => ({ ...prev, [entryId]: false }));
          setIsLoading(false);
        }
      };
    
      const handleOtherExpenseCheckboxClick = useCallback(
        async (entry, serverComplete, entriesInGroupOverride) => {
          if (
            isViewOnly ||
            entry?._isTrainingCost ||
            isTrainingExpenseDisplayRow(entry) ||
            loadingRows[entry._id]
          ) {
            return;
          }
          syncToolGuideStep("view-details");

          const sourceList =
            allDescriptionEntriesForProject?.length > 0
              ? allDescriptionEntriesForProject
              : displayHardwareDescriptions;
          const descTrim = normalizeOtherExpenseDescription(entry?.description);
          const entriesInGroup =
            entriesInGroupOverride ||
            (isManagerHardwareConsolidatedView || allDescriptionEntriesForProject?.length
              ? (sourceList || []).filter((e) =>
                  descriptionsMatch(e?.description, descTrim)
                )
              : [entry]);
          const groupServerComplete = entriesInGroup.every((e) =>
            isDescriptionTaskComplete(e)
          );
          const rowKey = entry._id;
          const revertCheckbox = () => {
            setDescriptionCompleteChecked((prev) => ({
              ...prev,
              [rowKey]: groupServerComplete,
            }));
          };

          const nextChecked = !(
            descriptionCompleteChecked[rowKey] ?? groupServerComplete
          );
          setDescriptionCompleteChecked((prev) => ({
            ...prev,
            [rowKey]: nextChecked,
          }));

          const currentExpenses = Number(
            String(editedDescription[entry._id] ?? entry.expenses ?? "").replace(
              /,/g,
              ""
            )
          );
          const startDate =
            updatedDateDescription[entry._id]?.startdate || entry.startdate;
          const endDate =
            updatedDateDescription[entry._id]?.enddate || entry.enddate;

          if (nextChecked) {
            if (!startDate || !endDate) {
              revertCheckbox();
              enqueueSnackbar("Please Select End Date", {
                variant: "error",
              });
              return;
            }
            if (!currentExpenses || currentExpenses <= 0) {
              revertCheckbox();
              enqueueSnackbar("Please Enter Expense", {
                variant: "error",
              });
              return;
            }

            setLoadingRows((prev) => ({ ...prev, [rowKey]: true }));
            setIsLoading(true);
            try {
              const useGroupFlow =
                isManagerHardwareConsolidatedView ||
                allDescriptionEntriesForProject?.length > 0;

              if (useGroupFlow) {
                const expenseSaved =
                  await persistManagerHardwareOtherExpenseBeforeComplete({
                    dispatch,
                    projectId: row?._id,
                    entry,
                    entriesInGroup,
                    allDescriptionEntriesForProject,
                    loggedUserId: loggedUser?._id,
                    currentExpenses,
                    startDate,
                    endDate,
                    editedDescription,
                    editedDescriptionNotes,
                  });
                if (!expenseSaved) {
                  revertCheckbox();
                  return;
                }
                await markHardwareOtherExpenseGroupComplete({
                  dispatch,
                  projectId: row?._id,
                  entriesInGroup,
                  managerEndDate: endDate ?? entry?.enddate ?? null,
                  managerStartDate: startDate ?? entry?.startdate ?? null,
                  complete: true,
                  loggedUserId: loggedUser?._id,
                  skipPreUpdate: true,
                });
              } else {
                await dispatch(
                  updateOngoingDescription({
                    projectId: row?._id,
                    id: entry._id,
                    updatedData: {
                      description: entry.description,
                      value: entry.value,
                      notes:
                        editedDescriptionNotes[entry._id] ?? entry.notes ?? "",
                      typeservice: entry.typeservice,
                      startdate: startDate ?? entry.startdate ?? null,
                      enddate: endDate ?? entry.enddate ?? null,
                      expenses: currentExpenses,
                    },
                  })
                ).unwrap();
                await markHardwareOtherExpenseGroupComplete({
                  dispatch,
                  projectId: row?._id,
                  entriesInGroup: [entry],
                  managerEndDate: endDate ?? entry?.enddate ?? null,
                  managerStartDate: startDate ?? entry?.startdate ?? null,
                  complete: true,
                  loggedUserId: loggedUser?._id,
                  skipPreUpdate: true,
                });
              }
              refresh();
              enqueueSnackbar("Description Completed Successfully", {
                variant: "success",
              });
            } catch {
              revertCheckbox();
              enqueueSnackbar("Failed To Complete Task", { variant: "error" });
            } finally {
              setLoadingRows((prev) => ({ ...prev, [rowKey]: false }));
              setIsLoading(false);
            }
            return;
          }

          setLoadingRows((prev) => ({ ...prev, [rowKey]: true }));
          setIsLoading(true);
          try {
            if (
              isManagerHardwareConsolidatedView ||
              allDescriptionEntriesForProject?.length > 0
            ) {
              await markHardwareOtherExpenseGroupComplete({
                dispatch,
                projectId: row?._id,
                entriesInGroup,
                complete: false,
                loggedUserId: loggedUser?._id,
              });
            } else {
              await dispatch(
                markNotDescriptionComplete({
                  projectid: row?._id,
                  taskid: entry._id,
                })
              ).unwrap();
              dispatch(fetchWorkEntriesProjectTracking());
              dispatch(fetchDescriptionTasksProjectTracking());
              dispatch(fetchHardwareProjectTracking());
            }
            refresh();
          } catch {
            revertCheckbox();
            enqueueSnackbar("Failed To Non-Complete Task", { variant: "error" });
          } finally {
            setLoadingRows((prev) => ({ ...prev, [rowKey]: false }));
            setIsLoading(false);
          }
        },
        [
          isViewOnly,
          loadingRows,
          descriptionCompleteChecked,
          editedDescription,
          editedDescriptionNotes,
          updatedDateDescription,
          enqueueSnackbar,
          setIsLoading,
          refresh,
          allDescriptionEntriesForProject,
          isManagerHardwareConsolidatedView,
          displayHardwareDescriptions,
          loggedUser?._id,
          dispatch,
          row?._id,
          syncToolGuideStep,
        ]
      );
    
  return (
    <div onClick={(e) => e.stopPropagation()} className="w-full flex-col">
          {showListState === "tasktable" && (
            <div className="w-full max-h-[24rem] overflow-y-auto overflow-x-hidden">
              <div className="sticky top-0 z-[999] flex bg-[#e8e8e8] text-black text-[10px] font-semibold break-all whitespace-normal">
                <div className="p-1 w-[10%] text-center">Part Name</div>
                <div className="p-1 w-[10%]">Part No</div>
                <div className="p-1 w-[10%] text-center">{" Quantity"} </div>
                <div className="p-1 w-[10%] text-center">
                  Total Price ({currency})
                </div>{" "}
                <div className="p-1 w-[10%] text-center">Start Date</div>
                <div className="p-1 w-[10%]">End Date</div>
                <div className="p-1 w-[30%] text-center">Tracker</div>
                <div className="p-1 w-[10%] text-center">Action</div>
              </div>
    
              <div className="flex text-[10px] break-all whitespace-normal max-h-14">
                <div className="p-1 text-center border-b border-[#dee2e6] w-[10%]">
                  {"Total"}
                </div>
                <div className="p-1 border-b border-[#dee2e6] w-[10%]"></div>
                <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
                  {USAComma(
                    hardwareProjectTrackingParts.reduce(
                      (sum, item) => sum + (item?.quantity || 0),
                      0
                    )
                  )}{" "}
                </div>
                <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
                  {USAComma(
                    hardwareProjectTrackingParts.reduce(
                      (sum, item) => sum + (item?.totalprice || 0),
                      0
                    )
                  )}
                </div>
                <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center"></div>
                <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center"></div>
                <div className="p-2.5 border-b border-[#dee2e6] w-[30%] text-center">
                  {(() => {
                    const allEntries = hardwareProjectTrackingParts.length;
                    const completedEntriesCount =
                      hardwareProjectTrackingParts.filter(
                        (entry) => entry.isTaskComplete
                      ).length;
                    const trackerPercentage =
                      allEntries > 0
                        ? (completedEntriesCount / allEntries) * 100
                        : 0;
                    return <ProgressTracker value={trackerPercentage} />;
                  })()}
                </div>
    
                <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center"></div>
              </div>
    
              {hardwareProjectTrackingParts.length > 0 ? (
                hardwareProjectTrackingParts.map((entry, index) => (
                  <div
                    key={entry}
                    className="flex text-[10px] break-all whitespace-normal max-h-14"
                  >
                    <div
                      className="p-1 border-b border-[#dee2e6] w-[10%] overflow-auto max-h-14"
                      title={`${entry.assemblepartdescription}`}
                    >
                      {entry.assemblepartdescription}
                    </div>
                    <div className="p-1 border-b border-[#dee2e6] w-[10%]">
                      {entry.partno}
                    </div>
                    <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
                      {/* {entry.quantity} */}
                      {entry.isTaskComplete ? (
                        <span>{entry.quantity || ""}</span>
                      ) : (
                        <input
                          type="text"
                          value={
                            updatedHardwareEntries[entry._id]?.quantity ??
                            entry.quantity ??
                            ""
                          }
                          placeholder="enter quantity"
                          className="border border-gray-200 w-full p-1 rounded-md text-center"
                          onChange={(e) => {
                            const qty = Number(e.target.value) || 0;
    
                            setUpdatedHardwareEntries((prev) => ({
                              ...prev,
                              [entry._id]: {
                                ...prev[entry._id],
                                quantity: qty,
                                totalprice: qty * entry.unitprice,
                              },
                            }));
                          }}
                          onBlur={() => handleHardwareQuantityUpdate(entry._id)}
                        />
                      )}
                    </div>
                    <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
                      {USAComma(
                        updatedHardwareEntries[entry._id]?.totalprice ??
                          entry.totalprice ??
                          0
                      )}
                    </div>
                    <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
                      {entry.isTaskComplete ? (
                        <div className="text-black">
                          {entry.startdate
                            ? formatDateByCountry(entry.startdate)
                            : ""}
                        </div>
                      ) : (
                        <DatePicker
                          dateFormat={getDateFormat()}
                          className="w-full h-full text-center outline-none border p-1 border-gray-300 rounded-md bg-white"
                          placeholderText={getDatePlaceholder()}
                          popperClassName="z-[1500]"
                          minDate={
                            entry.startdate
                              ? new Date(entry.startdate)
                              : row?.DateRequested
                                ? new Date(row.DateRequested)
                                : null
                          }
                          maxDate={new Date()}
                          selected={
                            updatedHardwareEntries[entry._id]?.startdate
                              ? updatedHardwareEntries[entry._id].startdate
                              : entry.startdate
                              ? new Date(entry.startdate)
                              : null
                          }
                          onChange={(date) =>
                            handleDateChange(date, "startdate", entry)
                          }
                        />
                      )}
                    </div>
                    <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
                      {entry.isTaskComplete || entry.enddate ? (
                        <div className="text-black">
                          {entry.enddate ? formatDateByCountry(entry.enddate) : ""}
                        </div>
                      ) : (
                        <DatePicker
                          dateFormat={getDateFormat()}
                          className="w-full h-full text-center outline-none border p-1 border-gray-300 rounded-md bg-white"
                          placeholderText={getDatePlaceholder()}
                          popperClassName="z-[1500]"
                          minDate={
                            // ✅ Use updated startdate if available, else fallback to entry.startdate
                            updatedHardwareEntries[entry._id]?.startdate
                              ? new Date(
                                  updatedHardwareEntries[entry._id].startdate
                                )
                              : entry.startdate
                              ? new Date(entry.startdate)
                              : null
                          }
                          selected={
                            updatedHardwareEntries[entry._id]?.enddate
                              ? updatedHardwareEntries[entry._id].enddate
                              : entry.enddate
                              ? new Date(entry.enddate)
                              : null
                          }
                          onChange={(date) =>
                            handleDateChange(date, "enddate", entry)
                          }
                        />
                      )}
                    </div>
                    <div className="p-2.5 border-b border-[#dee2e6] w-[30%] text-center">
                      <ProgressTracker value={entry.isTaskComplete ? 100 : 0} />
                    </div>
                    <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
                      {loadingRows[index] ? (
                        <LoadingSpinner />
                      ) : (
                        <Checkbox
                          checked={entry.isTaskComplete}
                          disabled={isViewOnly || row.Assignedperson?.every(
                            (person) => person.isProjectComplete === true
                          )}
                          onChange={(e) => {
                            if (isViewOnly) return;
                            const isChecked = e.target.checked;
                            const startDate =
                              updatedHardwareEntries[entry._id]?.startdate ||
                              entry.startdate;
                            const endDate =
                              updatedHardwareEntries[entry._id]?.enddate ||
                              entry.enddate;
                            if (isChecked) {
                              if (!startDate || !endDate) {
                                enqueueSnackbar("Please Select End Date", {
                                  variant: "error",
                                });
                                return;
                              }
                              handleHardwarePartsMarkComplete(entry, index);
                              handleDateSelfEntryHardware(entry);
                            } else {
                              handleMarkNonCompleteHardware(entry);
                            }
                          }}
                        />
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="flex text-[14px] break-all whitespace-normal max-h-14 p-1">
                  <p className="text-red-500 text-center">
                    {" "}
                    No Tracking Task Entries Found
                  </p>
                </div>
              )}
            </div>
          )}
    
          {showListState === "otherexpenses" && (
            <div className="w-full max-h-[24rem] overflow-y-auto overflow-x-hidden">
              <div className="sticky z-[999] top-0 flex bg-[#e8e8e8] text-black text-[10px] font-semibold break-all whitespace-normal ">
                <div className="p-1 w-[10%] text-center">Description</div>
                <div className="p-1 w-[10%] text-center">Amount ({currency})</div>
    
                <div className="p-1 w-[10%] text-center">Start Date</div>
                <div className="p-1 w-[10%]">End Date</div>
                <div className="p-1 w-[10%] text-center">Notes</div>
                <div className="p-1 w-[10%] text-center">Expenses ({currency})</div>
                <div className="p-1 w-[30%] text-center">Tracker</div>
                <div className="p-1  w-[10%] text-center">Action</div>
              </div>
    
              <div className="flex text-[10px] break-all whitespace-normal max-h-14">
                <div className="p-1 text-center border-b border-[#dee2e6] w-[10%]">
                  {"Total"}
                </div>
                <div className="p-1 border-b border-[#dee2e6] w-[10%]">
                  {USAComma(
                    displayHardwareDescriptions.reduce(
                      (sum, item) => sum + (item?.value || 0),
                      0
                    )
                  )}
                </div>
                <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center"></div>
                <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center"></div>
                <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center"></div>
                <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
                  {USAComma(
                    displayHardwareDescriptions.reduce(
                      (sum, item) => sum + (item?.expenses || 0),
                      0
                    )
                  )}
                </div>
                <div className="p-2.5 border-b border-[#dee2e6] w-[30%] text-center">
                  {displayHardwareDescriptions.every((entry) => entry.isTaskComplete) ? (
                    <>
                      {(() => {
                        const hoursAssigned = displayHardwareDescriptions.reduce(
                          (sum, item) => sum + (item?.value || 0),
                          0
                        );
                        const hoursSpent = displayHardwareDescriptions.reduce(
                          (sum, item) => sum + (item?.expenses || 0),
                          0
                        );
    
                        const trackerPercentage =
                          hoursAssigned > 0
                            ? (hoursSpent / hoursAssigned) * 100
                            : 0;
    
                        const displayValue =
                          trackerPercentage < 100 ? 100 : trackerPercentage;
    
                        return <ProgressTracker value={displayValue} />;
                      })()}{" "}
                    </>
                  ) : (
                    <>
                      {(() => {
                        const allEntries = displayHardwareDescriptions?.reduce(
                          (sum, item) => sum + (item?.value || 0),
                          0
                        );
                        const completedEntriesCount = displayHardwareDescriptions?.reduce(
                          (sum, item) => sum + (item?.expenses || 0),
                          0
                        );
                        const trackerPercentage =
                          allEntries > 0
                            ? (completedEntriesCount / allEntries) * 100
                            : 0;
                        return <ProgressTracker value={trackerPercentage} />;
                      })()}
                    </>
                  )}
                </div>
                <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center"></div>
              </div>
    
              {displayHardwareDescriptions.length > 0 ? (
                displayHardwareDescriptions.map((entry, index) => {
                  const sourceList =
                    allDescriptionEntriesForProject?.length > 0
                      ? allDescriptionEntriesForProject
                      : displayHardwareDescriptions;
                  const descTrim = normalizeOtherExpenseDescription(
                    entry?.description
                  );
                  const entriesInGroup =
                    isManagerHardwareConsolidatedView ||
                    allDescriptionEntriesForProject?.length > 0
                      ? (sourceList || []).filter((e) =>
                          descriptionsMatch(e?.description, descTrim)
                        )
                      : [entry];
                  const serverComplete = entriesInGroup.every((e) =>
                    isDescriptionTaskComplete(e)
                  );
                  const checkboxChecked =
                    descriptionCompleteChecked[entry._id] ?? serverComplete;
                  const isTrainingRow =
                    entry?._isTrainingCost || isTrainingExpenseDisplayRow(entry);
                  const currentExpenseForTracker = Number(
                    String(
                      editedDescription[entry._id] ?? entry.expenses ?? ""
                    ).replace(/,/g, "")
                  );

                  return (
                  <div
                    key={entry._id || index}
                    className="flex text-[10px] break-all whitespace-normal max-h-14"
                  >
                    <div
                      className="p-1 border-b border-[#dee2e6] w-[10%] overflow-auto max-h-14"
                      title={`${entry.description}`}
                    >
                      {entry.description}
                    </div>
                    <div className="p-1 border-b border-[#dee2e6] w-[10%]">
                      {USAComma(entry.value)}
                    </div>
                    <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
                      {entry.isTaskComplete ? (
                        <div className="text-black">
                          {entry.startdate
                            ? formatDateByCountry(entry.startdate)
                            : ""}
                        </div>
                      ) : (
                        <DatePicker
                          dateFormat={getDateFormat()}
                          className="w-full h-full text-center outline-none border p-1 border-gray-300 rounded-md bg-white"
                          placeholderText={getDatePlaceholder()}
                          popperClassName="z-[1500]"
                          minDate={
                            entry.startdate
                              ? new Date(entry.startdate)
                              : row?.DateRequested
                                ? new Date(row.DateRequested)
                                : null
                          }
                          maxDate={new Date()}
                          selected={
                            updatedDateDescription[entry._id]?.startdate
                              ? updatedDateDescription[entry._id].startdate
                              : entry.startdate
                              ? new Date(entry.startdate)
                              : null
                          }
                          onChange={(date) =>
                            handleDescriptionDateChange(date, "startdate", entry)
                          }
                        />
                      )}
                    </div>
                    <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
                      {entry.isTaskComplete || entry.enddate ? (
                        <div className="text-black">
                          {entry.enddate ? formatDateByCountry(entry.enddate) : ""}
                        </div>
                      ) : (
                        <DatePicker
                          dateFormat={getDateFormat()}
                          className="w-full h-full text-center outline-none border p-1 border-gray-300 rounded-md bg-white"
                          placeholderText={getDatePlaceholder()}
                          popperClassName="z-[1500]"
                          minDate={
                            // ✅ Use updated startdate if available, else fallback to entry.startdate
                            updatedDateDescription[entry._id]?.startdate
                              ? new Date(
                                  updatedDateDescription[entry._id].startdate
                                )
                              : entry.startdate
                              ? new Date(entry.startdate)
                              : null
                          }
                          selected={
                            updatedDateDescription[entry._id]?.enddate
                              ? updatedDateDescription[entry._id].enddate
                              : entry.enddate
                              ? new Date(entry.enddate)
                              : null
                          }
                          onChange={(date) =>
                            handleDescriptionDateChange(date, "enddate", entry)
                          }
                        />
                      )}
                    </div>
                    <div
                      className="p-1 border-b border-[#dee2e6] w-[10%] text-center max-h-14 overflow-auto"
                      title={`${entry.notes}`}
                    >
                      {/* {entry.notes} */}
                      {entry.isTaskComplete ? (
                        <span>{entry.notes || ""}</span>
                      ) : (
                        <input
                          type="text"
                          value={
                            editedDescriptionNotes[entry._id] ?? entry.notes ?? ""
                          }
                          placeholder="enter note"
                          className="border border-gray-200 placeholder-text-gray-400 text-black w-full max-w-full p-1 rounded-md text-center"
                          onChange={(e) => {
                            const value = e.target.value;
                            setEditedDescriptionNotes((prev) => ({
                              ...prev,
                              [entry._id]: value,
                            }));
                          }}
                          onBlur={() => handleDescriptionNotesUpdate(entry._id)}
                        />
                      )}
                    </div>
                    <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
                      {entry.isTaskComplete ? (
                        <span>{USAComma(entry.expenses) || ""}</span>
                      ) : (
                        <input
                          type="text"
                          value={
                            editedDescription[entry._id] ??
                            USAComma(entry.expenses) ??
                            ""
                          }
                          placeholder="enter expense"
                          className="border border-gray-200 w-full rounded-md max-w-full p-1 text-center"
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value > entry.value) {
                              enqueueSnackbar("Expenses is exceeding the Amount", {
                                variant: "error",
                              });
                            }
                            if (Number(value) < 0) return;
                            setEditedDescription((prev) => ({
                              ...prev,
                              [entry._id]: value, // store by task id
                            }));
                          }}
                          onBlur={() => handleDescriptionExpensesUpdate(entry._id)}
                        />
                      )}
                    </div>
                    <div className="p-2.5 border-b border-[#dee2e6] w-[30%] text-center">
                      {entry.isTaskComplete ? (
                        <ProgressTracker
                          value={(() => {
                            const rawValue =
                              ((Number(entry.expenses) || 0) / entry.value) * 100;
                            return rawValue < 100 ? 100 : rawValue;
                          })()}
                        />
                      ) : (
                        <ProgressTracker
                          value={
                            ((Number(
                              editedDescription[entry._id] ?? entry.expenses
                            ) || 0) /
                              entry.value) *
                            100
                          }
                        />
                      )}
                    </div>
                    <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
                      {loadingRows[entry._id] ? (
                        <LoadingSpinner />
                      ) : isTrainingRow ? (
                        <Checkbox disabled checked={serverComplete} />
                      ) : (
                        <Checkbox
                          disabled={
                            isViewOnly ||
                            !!loadingRows[entry._id] ||
                            row.Assignedperson?.every(
                              (person) => person.isProjectComplete === true
                            ) ||
                            (!serverComplete &&
                              ((currentExpenseForTracker || 0) /
                                (Number(entry.value) || 1)) *
                                100 <
                                1)
                          }
                          checked={checkboxChecked}
                          sx={
                            serverComplete
                              ? {
                                  color: "grey.500",
                                  "&.Mui-checked": { color: "grey.600" },
                                }
                              : undefined
                          }
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            void handleOtherExpenseCheckboxClick(
                              entry,
                              serverComplete,
                              entriesInGroup
                            );
                          }}
                        />
                      )}
                    </div>
                  </div>
                  );
                })
              ) : (
                <div className="flex text-[14px] break-all whitespace-normal max-h-14 p-1">
                  <p className="text-red-500 text-center">
                    {" "}
                    No Tracking Task Entries Found
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
  )
}

export default OPHardwareOngoingPM